import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { Mail, CheckCircle } from 'lucide-react';
import { useAuthStore } from '../../../stores/authStore';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { PublicHelpButton } from '../../../components/help/PublicHelpButton';

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function RegisterForm() {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
  });

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError();
    setValidationError(null);
  };

  const validateForm = (): boolean => {
    // Check if Turnstile is completed (if configured)
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setValidationError('Bitte bestätigen Sie, dass Sie kein Roboter sind');
      return false;
    }

    // Check if terms are accepted
    if (!acceptedTerms) {
      setValidationError('Bitte akzeptieren Sie die AGB und Richtlinien');
      return false;
    }

    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwörter stimmen nicht überein');
      return false;
    }

    // Check password strength
    if (formData.password.length < 8) {
      setValidationError('Passwort muss mindestens 8 Zeichen lang sein');
      return false;
    }

    if (!/[A-Z]/.test(formData.password)) {
      setValidationError('Passwort muss mindestens einen Großbuchstaben enthalten');
      return false;
    }

    if (!/[a-z]/.test(formData.password)) {
      setValidationError('Passwort muss mindestens einen Kleinbuchstaben enthalten');
      return false;
    }

    if (!/[0-9]/.test(formData.password)) {
      setValidationError('Passwort muss mindestens eine Zahl enthalten');
      return false;
    }

    if (!/[^A-Za-z0-9]/.test(formData.password)) {
      setValidationError('Passwort muss mindestens ein Sonderzeichen enthalten');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const response = await register(
        {
          email: formData.email,
          password: formData.password,
          displayName: formData.displayName || undefined,
        },
        turnstileToken || undefined
      );

      // Show success message - user needs to verify email
      if (response.requiresVerification) {
        setRegistrationSuccess(true);
      }
    } catch (error) {
      // Error is handled by the store
      console.error('Registration error:', error);
      // Reset Turnstile on error
      turnstileRef.current?.reset();
      setTurnstileToken(null);
    }
  };

  const displayError = validationError || error;

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <>
        <PublicHelpButton />
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle>Registrierung erfolgreich!</CardTitle>
            <CardDescription className="text-base">
              Wir haben Ihnen eine E-Mail an <strong>{formData.email}</strong> gesendet.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg text-center">
              <Mail className="h-6 w-6 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Bitte klicken Sie auf den Link in der E-Mail, um Ihre Registrierung abzuschließen.
              </p>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Keine E-Mail erhalten? Prüfen Sie Ihren Spam-Ordner oder{' '}
              <button
                type="button"
                onClick={() => setRegistrationSuccess(false)}
                className="text-primary hover:underline"
              >
                versuchen Sie es erneut
              </button>
              .
            </p>
          </CardContent>

          <CardFooter>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Zurück zur Anmeldung
            </Button>
          </CardFooter>
        </Card>
      </>
    );
  }

  return (
    <>
    <PublicHelpButton />
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>Konto erstellen</CardTitle>
        <CardDescription>
          Erstellen Sie ein neues Konto, um zu beginnen
        </CardDescription>
      </CardHeader>

      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-4">
          {displayError && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
              {displayError}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="ihre@email.de"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Name (Optional)</Label>
            <Input
              id="displayName"
              name="displayName"
              type="text"
              placeholder="Max Mustermann"
              value={formData.displayName}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Passwort</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Mind. 8 Zeichen, mit Groß-/Kleinbuchstaben, Zahl und Sonderzeichen
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          {TURNSTILE_SITE_KEY && (
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={TURNSTILE_SITE_KEY}
                onSuccess={(token: string) => {
                  setTurnstileToken(token);
                  setValidationError(null);
                }}
                onError={() => {
                  setTurnstileToken(null);
                  setValidationError('Verifizierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
                }}
                onExpire={() => {
                  setTurnstileToken(null);
                }}
                options={{
                  theme: 'auto',
                  language: 'de',
                }}
              />
            </div>
          )}

          <div className="flex items-start space-x-2">
            <Checkbox
              id="acceptTerms"
              checked={acceptedTerms}
              onCheckedChange={(checked) => {
                setAcceptedTerms(checked === true);
                clearError();
                setValidationError(null);
              }}
              disabled={isLoading}
            />
            <label
              htmlFor="acceptTerms"
              className="text-sm leading-tight cursor-pointer"
            >
              Ich akzeptiere die{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                AGB und Richtlinien
              </a>
            </label>
          </div>
        </CardContent>

        <CardFooter className="flex flex-col space-y-4">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Registrieren...' : 'Registrieren'}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            Bereits ein Konto?{' '}
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-primary hover:underline"
              disabled={isLoading}
            >
              Jetzt anmelden
            </button>
          </p>
        </CardFooter>
      </form>
    </Card>
    </>
  );
}
